<script setup lang="ts">
import CInput from '@/components/shared/CInput';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import CalendarStrokeField from './CalendarStrokeField.vue';
import { useCalendarConfig } from './useCalendarConfig';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig, CalendarStrokeValue } from './types';

const BACKGROUND_MODES: CButtonGroupItem<boolean>[] = [
    { label: 'Unified Background', value: true },
    { label: 'Separate Background Colors', value: false }
];

const SEPARATE_BACKGROUNDS: {
    title: string;
    key: 'multiBGYearMonthColor' | 'multiBGDaysTitlesColor' | 'multiBGDaysColor';
}[] = [
    { title: 'Month & Year Background', key: 'multiBGYearMonthColor' },
    { title: 'Day Titles Background', key: 'multiBGDaysTitlesColor' },
    { title: 'Days Background', key: 'multiBGDaysColor' }
];

// Vue 2.7 SFC compiler cannot resolve imported types in defineProps
const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
}>();
const emit = defineEmits<{
    (e: 'update-config', config: Partial<CalendarConfig>): void;
}>();

const { config, elementKey, disabled, update } = useCalendarConfig(props, emit);

const onStrokeUpdate = ({ width, color }: CalendarStrokeValue) => {
    update({ BGBorderWidth: width, BGBorderColor: color });
};
</script>

<template>
    <div v-if="config" :key="elementKey" class="calendar-section">
        <c-input
            :value="Number(config.borderRadius)"
            type="decimal"
            step=".1"
            :min="0"
            label="Border Radius"
            :disabled="disabled"
            @input="update({ borderRadius: String($event) })"
        />
        <calendar-stroke-field
            title="Calendar Stroke"
            :width="config.BGBorderWidth"
            :color="config.BGBorderColor"
            :disabled="disabled"
            @update="onStrokeUpdate"
        />
        <calendar-field-title>Background</calendar-field-title>
        <c-button-group
            :value="config.useSingleBG"
            no-icon
            type="radio"
            wide
            :items="BACKGROUND_MODES"
            :disabled="disabled"
            @input="update({ useSingleBG: $event })"
        />
        <calendar-color-field
            v-if="config.useSingleBG"
            :value="config.singleBGColor"
            :disabled="disabled"
            @input="update({ singleBGColor: $event })"
        />
        <template v-else>
            <calendar-color-field
                v-for="background in SEPARATE_BACKGROUNDS"
                :key="background.key"
                :title="background.title"
                :value="config[background.key]"
                :disabled="disabled"
                @input="update({ [background.key]: $event })"
            />
        </template>
    </div>
</template>

<style scoped lang="scss">
@import './calendarSection';
</style>
